/**
 * Review count / rating shown on the listing page.
 *
 * `publicData.finalReviews` is a stored snapshot ({ rating, count }) written by several places:
 * the Google place import in the location editor and /api/update-review on new socialbnb reviews.
 * The location editor overwrites it with Google-only numbers, so a listing that also has socialbnb
 * reviews ends up with a badge that says "3 reviews" while the reviews modal lists five.
 *
 * The listing page displays two sources: the host's socialbnb reviews (`reviews`, fetched by host
 * id) and the listing's Google reviews (`publicData.location`). This helper derives the count and
 * the rating from exactly those two sources, so the badge, the impact card, the host section, the
 * booking panel and the modal always agree with each other.
 *
 * The stored snapshot is used only as a floor: if it claims more reviews than we can reconstruct
 * (older listings whose Google totals were never written to `location`), we keep the snapshot
 * rather than show a smaller number.
 */

const round1 = n => Math.round(n * 10) / 10;

const toPositiveNumber = value => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export const getListingReviewStats = (currentListing, reviews = []) => {
  const publicData = currentListing?.attributes?.publicData || {};
  const stored = publicData.finalReviews || {};
  const storedCount = toPositiveNumber(stored.count);
  const storedRating = toPositiveNumber(stored.rating);

  const internalRatings = (Array.isArray(reviews) ? reviews : [])
    .map(r => toPositiveNumber(r?.attributes?.rating ?? r?.rating))
    .filter(n => n > 0);
  const internalCount = internalRatings.length;
  const internalSum = internalRatings.reduce((sum, n) => sum + n, 0);

  const location = publicData.location || {};
  const googleRating = toPositiveNumber(location.rating);
  const googleReviewsList = Array.isArray(location.reviews) ? location.reviews : [];
  const googleCount =
    googleRating > 0 ? toPositiveNumber(location.totalGoogleReviews) || googleReviewsList.length : 0;

  const derivedCount = internalCount + googleCount;

  if (derivedCount === 0 || storedCount > derivedCount) {
    return { count: storedCount, rating: storedRating, internalCount, googleCount };
  }

  const rating = round1((internalSum + googleRating * googleCount) / derivedCount);
  return { count: derivedCount, rating, internalCount, googleCount };
};
